import { 
  HelpCircle, 
  Clock, 
  MapPin, 
  Camera, 
  Calendar,
  Users,
  Shield,
  CheckCircle,
  AlertTriangle,
  Phone,
  Mail,
  MessageSquare,
  CalendarPlus,
  LogIn,
  LogOut,
  Search,
  Eye,
  Briefcase
} from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { useAuth } from '../../contexts/AuthContext';
import AdminLayout from '../../components/layout/AdminLayout';
import SupervisorLayout from '../../components/layout/SupervisorLayout';
import EmployeeLayout from '../../components/layout/EmployeeLayout';

export default function HelpPage() {
  const { user } = useAuth();
  const role = user?.role;

  const content = (
    <div className="p-6 space-y-6 max-w-3xl mx-auto">
      {/* Header */}
      <div className="text-center border-b border-border pb-6">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-4">
          <HelpCircle className="w-8 h-8 text-primary" />
        </div>
        <h1 className="text-2xl font-barlow tracking-widest uppercase">Help Guide</h1>
        <p className="text-sm text-muted-foreground mt-2">
          {role === 'admin' && 'Admin Guide - Manage your security team'}
          {role === 'supervisor' && 'Supervisor Guide - Clock staff in/out'}
          {role === 'employee' && 'Employee Guide - How to use the app'}
        </p>
      </div>

      {/* Employee Guide */}
      {role === 'employee' && (
        <>
          {/* 1) QUICK OVERVIEW */}
          <Card className="bg-card border-border rounded-sm">
            <CardContent className="p-5">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-blue-500/10 rounded-sm">
                  <Phone className="w-5 h-5 text-blue-400" />
                </div>
                <h2 className="font-barlow tracking-wider uppercase text-lg">Quick Overview</h2>
              </div>
              <p className="text-sm text-muted-foreground mb-3">This app allows you to:</p>
              <ul className="space-y-2">
                {[
                  'Clock in and clock out',
                  'View your scheduled shifts',
                  'Claim available shifts',
                  'Access company policies and procedures',
                  'Contact Admin when needed',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2 text-sm">
                    <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>

          {/* 2) IMPORTANT RULES */}
          <Card className="bg-card border-yellow-500/30 rounded-sm">
            <CardContent className="p-5">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-yellow-500/10 rounded-sm">
                  <AlertTriangle className="w-5 h-5 text-yellow-400" />
                </div>
                <h2 className="font-barlow tracking-wider uppercase text-lg">Important Rules</h2>
              </div>
              <ul className="space-y-2 text-sm">
                {[
                  'Always take a selfie — you cannot clock in/out without it.',
                  "Be on location — photos prove you're at the work site.",
                  'Clock in/out on time — all times are recorded.',
                  'No shift scheduled? Contact your supervisor or Admin.',
                ].map((rule, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="text-yellow-400 font-bold">{i + 1}.</span>
                    <span>{rule}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>

          {/* 3) COMMON TASKS */}
          <Card className="bg-card border-border rounded-sm">
            <CardContent className="p-5">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-purple-500/10 rounded-sm">
                  <Briefcase className="w-5 h-5 text-purple-400" />
                </div>
                <h2 className="font-barlow tracking-wider uppercase text-lg">Common Tasks</h2>
              </div>
              <div className="space-y-4">
                <div className="border-l-2 border-green-500 pl-4">
                  <p className="font-medium text-green-400 flex items-center gap-2">
                    <LogIn className="w-4 h-4" /> Clock In
                  </p>
                  <ol className="text-sm text-muted-foreground mt-2 space-y-1 list-decimal list-inside">
                    <li>Take a selfie.</li>
                    <li>Press CLOCK IN.</li>
                  </ol>
                </div>

                <div className="border-l-2 border-red-500 pl-4">
                  <p className="font-medium text-red-400 flex items-center gap-2">
                    <LogOut className="w-4 h-4" /> Clock Out
                  </p>
                  <ol className="text-sm text-muted-foreground mt-2 space-y-1 list-decimal list-inside">
                    <li>Take another selfie.</li>
                    <li>Press CLOCK OUT.</li>
                  </ol>
                </div>

                <div className="border-l-2 border-blue-500 pl-4">
                  <p className="font-medium text-blue-400 flex items-center gap-2">
                    <CalendarPlus className="w-4 h-4" /> Claim Extra Shifts
                  </p>
                  <ol className="text-sm text-muted-foreground mt-2 space-y-1 list-decimal list-inside">
                    <li>Open Available Shifts.</li>
                    <li>Press Claim.</li>
                  </ol>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* 4) SEND PHOTO TO ADMIN */}
          <Card className="bg-card border-border rounded-sm">
            <CardContent className="p-5">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-green-500/10 rounded-sm">
                  <MessageSquare className="w-5 h-5 text-green-400" />
                </div>
                <h2 className="font-barlow tracking-wider uppercase text-lg">Send Photo to Admin</h2>
              </div>
              <p className="text-sm text-muted-foreground mb-2">When you arrive on site:</p>
              <ol className="text-sm space-y-1 list-decimal list-inside">
                <li>Take a photo of the site.</li>
                <li>Send the photo to Admin on WhatsApp.</li>
                <li>Then clock in on the app.</li>
              </ol>
              <p className="text-xs text-green-400 mt-3">
                This confirms your arrival to management.
              </p>
            </CardContent>
          </Card>

          {/* 5) FREQUENTLY ASKED QUESTIONS */}
          <Card className="bg-card border-border rounded-sm">
            <CardContent className="p-5">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-cyan-500/10 rounded-sm">
                  <HelpCircle className="w-5 h-5 text-cyan-400" />
                </div>
                <h2 className="font-barlow tracking-wider uppercase text-lg">Frequently Asked Questions</h2>
              </div>
              <div className="space-y-3">
                {[
                  {
                    q: "I don't have a shift today. What should I do?",
                    a: 'Contact Admin or your supervisor.',
                  },
                  {
                    q: 'Can I clock in early?',
                    a: 'Yes. Early arrivals are rounded to the official shift start time.',
                  },
                  {
                    q: 'I forgot to clock out. What happens?',
                    a: 'The system will automatically clock you out.',
                  },
                  {
                    q: 'Can I use Manual Clock In?',
                    a: 'Only when instructed by Admin or a supervisor.',
                  },
                ].map((item, i) => (
                  <div key={i} className="border-l-2 border-cyan-500/40 pl-3 py-1">
                    <p className="text-sm font-medium text-foreground">{item.q}</p>
                    <p className="text-sm text-muted-foreground mt-1">{item.a}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* 6) NEED MORE HELP — employee-specific contact card */}
          <Card className="bg-gradient-to-r from-primary/5 to-blue-500/5 border-primary/20 rounded-sm">
            <CardContent className="p-5">
              <div className="flex items-center gap-3 mb-3">
                <HelpCircle className="w-5 h-5 text-primary" />
                <h2 className="font-barlow tracking-wider uppercase text-lg">Need More Help?</h2>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-3 p-3 bg-card/50 rounded-sm border border-border">
                  <Mail className="w-4 h-4 text-primary shrink-0" />
                  <span>Contact Admin</span>
                </div>
                <div className="flex items-center gap-3 p-3 bg-card/50 rounded-sm border border-border">
                  <Phone className="w-4 h-4 text-primary shrink-0" />
                  <span>Contact your supervisor</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {/* Supervisor Guide */}
      {role === 'supervisor' && (
        <>
          <Card className="bg-card border-border rounded-sm">
            <CardContent className="p-5">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-blue-500/10 rounded-sm">
                  <Shield className="w-5 h-5 text-blue-400" />
                </div>
                <h2 className="font-barlow tracking-wider uppercase text-lg">Your Role</h2>
              </div>
              <p className="text-muted-foreground text-sm leading-relaxed">
                As a Supervisor, you can:
              </p>
              <ul className="mt-3 space-y-2">
                <li className="flex items-center gap-2 text-sm">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  Clock employees in/out using their codes
                </li>
                <li className="flex items-center gap-2 text-sm">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  Add comments/notes to clock events
                </li>
                <li className="flex items-center gap-2 text-sm">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  Create and manage shifts
                </li>
                <li className="flex items-center gap-2 text-sm">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  View employee photos
                </li>
                <li className="flex items-center gap-2 text-sm">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  Pick up available shifts for yourself
                </li>
              </ul>
            </CardContent>
          </Card>

          <Card className="bg-card border-border rounded-sm">
            <CardContent className="p-5">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-green-500/10 rounded-sm">
                  <Clock className="w-5 h-5 text-green-400" />
                </div>
                <h2 className="font-barlow tracking-wider uppercase text-lg">How to Clock Staff</h2>
              </div>
              <div className="space-y-4">
                <div className="border-l-2 border-primary pl-4">
                  <p className="font-medium">Option 1: Using Employee Code</p>
                  <ol className="text-sm text-muted-foreground mt-2 space-y-1 list-decimal list-inside">
                    <li>Enter the employee's 4-digit code</li>
                    <li>Click "Clock In" or "Clock Out"</li>
                    <li>Add a comment if needed</li>
                  </ol>
                </div>
                <div className="border-l-2 border-blue-500 pl-4">
                  <p className="font-medium">Option 2: From Staff List</p>
                  <ol className="text-sm text-muted-foreground mt-2 space-y-1 list-decimal list-inside">
                    <li>Find the employee in the list</li>
                    <li>Add a comment/note (optional)</li>
                    <li>Click "CLOCK IN" or "CLOCK OUT"</li>
                  </ol>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-card border-border rounded-sm">
            <CardContent className="p-5">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-purple-500/10 rounded-sm">
                  <CalendarPlus className="w-5 h-5 text-purple-400" />
                </div>
                <h2 className="font-barlow tracking-wider uppercase text-lg">Managing Shifts</h2>
              </div>
              <ul className="space-y-2 text-sm">
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  Go to "Create Shifts" to assign shifts to employees
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  Select multiple dates at once for the same employee
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  Go to "Upcoming Shifts" to create open shifts staff can claim
                </li>
              </ul>
            </CardContent>
          </Card>
        </>
      )}

      {/* Admin Guide */}
      {role === 'admin' && (
        <>
          <Card className="bg-card border-border rounded-sm">
            <CardContent className="p-5">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-primary/10 rounded-sm">
                  <Shield className="w-5 h-5 text-primary" />
                </div>
                <h2 className="font-barlow tracking-wider uppercase text-lg">Admin Features</h2>
              </div>
              <ul className="space-y-2">
                <li className="flex items-center gap-2 text-sm">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  Manage employees and supervisors
                </li>
                <li className="flex items-center gap-2 text-sm">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  Create and assign shifts
                </li>
                <li className="flex items-center gap-2 text-sm">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  View and edit clock history
                </li>
                <li className="flex items-center gap-2 text-sm">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  Download payroll reports
                </li>
                <li className="flex items-center gap-2 text-sm">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  Create upcoming/open shifts
                </li>
                <li className="flex items-center gap-2 text-sm">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  Manage transport/journeys
                </li>
              </ul>
            </CardContent>
          </Card>

          <Card className="bg-card border-border rounded-sm">
            <CardContent className="p-5">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-blue-500/10 rounded-sm">
                  <Users className="w-5 h-5 text-blue-400" />
                </div>
                <h2 className="font-barlow tracking-wider uppercase text-lg">Managing Staff</h2>
              </div>
              <div className="space-y-3 text-sm">
                <div className="p-3 bg-accent/50 rounded-sm">
                  <p className="font-medium text-primary">Invite Employee</p>
                  <p className="text-muted-foreground text-xs mt-1">Click "Invite Employee" → Enter name & email → Share the link</p>
                </div>
                <div className="p-3 bg-accent/50 rounded-sm">
                  <p className="font-medium text-blue-400">Add Supervisor</p>
                  <p className="text-muted-foreground text-xs mt-1">Click "Add Supervisor" → Enter name & email → Share the link</p>
                </div>
                <div className="p-3 bg-accent/50 rounded-sm">
                  <p className="font-medium text-green-400">Generate Codes</p>
                  <p className="text-muted-foreground text-xs mt-1">Click "Generate All Codes" to create 4-digit codes for all employees</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-card border-border rounded-sm">
            <CardContent className="p-5">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-green-500/10 rounded-sm">
                  <CalendarPlus className="w-5 h-5 text-green-400" />
                </div>
                <h2 className="font-barlow tracking-wider uppercase text-lg">Creating Shifts</h2>
              </div>
              <div className="space-y-3 text-sm">
                <div className="border-l-2 border-primary pl-4">
                  <p className="font-medium">Shift Calendar (Assign to specific employee)</p>
                  <p className="text-muted-foreground text-xs mt-1">Select employee → Pick dates → Set times → Create</p>
                </div>
                <div className="border-l-2 border-blue-500 pl-4">
                  <p className="font-medium">Upcoming Shifts (Open for staff to claim)</p>
                  <p className="text-muted-foreground text-xs mt-1">Set location, dates, times, pay rate → Staff can claim available slots</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-card border-border rounded-sm">
            <CardContent className="p-5">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-purple-500/10 rounded-sm">
                  <Eye className="w-5 h-5 text-purple-400" />
                </div>
                <h2 className="font-barlow tracking-wider uppercase text-lg">Viewing & Editing</h2>
              </div>
              <ul className="space-y-2 text-sm">
                <li className="flex items-center gap-2">
                  <Camera className="w-4 h-4 text-muted-foreground" />
                  Click camera icon to view employee clock-in photos
                </li>
                <li className="flex items-center gap-2">
                  <Eye className="w-4 h-4 text-muted-foreground" />
                  Click eye icon to show/hide employee email
                </li>
                <li className="flex items-center gap-2">
                  <Search className="w-4 h-4 text-muted-foreground" />
                  Use search bar to find staff by name or email
                </li>
              </ul>
            </CardContent>
          </Card>
        </>
      )}

      {/* Contact — generic fallback, shown only for admin/supervisor.
          Employees have their own dedicated "Need More Help?" card above. */}
      {role !== 'employee' && (
        <Card className="bg-gradient-to-r from-primary/5 to-blue-500/5 border-primary/20 rounded-sm">
          <CardContent className="p-5 text-center">
            <HelpCircle className="w-8 h-8 text-primary mx-auto mb-3" />
            <h3 className="font-medium">Need More Help?</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Contact your system administrator for assistance.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );

  // Return with appropriate layout based on role
  if (role === 'admin') {
    return <AdminLayout>{content}</AdminLayout>;
  } else if (role === 'supervisor') {
    return <SupervisorLayout>{content}</SupervisorLayout>;
  } else {
    return <EmployeeLayout>{content}</EmployeeLayout>;
  }
}
